import { createTheme } from '@mui/material';
import { createBreakpoints } from '@mui/system';
import * as tkn from './theme-constants';

const spacing = function (value: number): number {
    return 8 * value;
};

// use 600 for medium on windows, otherwise its 500 - do not remove without consulting Sharon
const boldWeight: number = navigator.userAgent.includes('Win') ? 600 : 500;

const breakpoints = createBreakpoints({});

// let's try rem font sizing
const fontSizeBase = 16; // 16px - this one has to be in pixels
const fontSizeBody = '0.875rem'; // 14px
const fontSizeSmall = '0.75rem'; // 12px
const fontSizeH1 = '3rem';
const fontSizeH2 = '2.625rem';
const fontSizeH3 = '2.25rem';
const fontSizeH4 = '1.875rem';
const fontSizeH5 = '1.5rem';
const fontSizeH6 = '1.25rem';

const light = 300;
const regular = 400;
const bold = boldWeight;
const extraBold = 700;

const borderRadius = 4;
const boxShadow = '0 0.5px 2px rgba(0, 0, 0, 0.16)';
const buttonHeight = '40px';
const buttonPadding = '0 16px';
const smallButtonHeight = '32px';
const smallButtonPadding = '0 8px';

export const stitchLight = createTheme({
    palette: {
        mode: 'light',
        common: {
            black: tkn.COMMON_BLACK,
            white: tkn.COMMON_WHITE,
        },
        primary: {
            main: tkn.LM_PRIMARY_MAIN,
            light: tkn.LM_PRIMARY_LIGHT,
            dark: tkn.LM_PRIMARY_DARK,
            contrastText: tkn.LM_PRIMARY_CONTRAST_TEXT,
            hover: tkn.LM_PRIMARY_HOVER,
            focus: tkn.LM_PRIMARY_FOCUS,
            focusVisible: tkn.LM_PRIMARY_FOCUS_VISIBLE,
            selected: tkn.LM_PRIMARY_SELECTED,
        },
        secondary: {
            main: tkn.LM_SECONDARY_MAIN,
            light: tkn.LM_SECONDARY_LIGHT,
            dark: tkn.LM_SECONDARY_DARK,
            contrastText: tkn.LM_SECONDARY_CONTRAST_TEXT,
            hover: tkn.LM_SECONDARY_HOVER,
            focus: tkn.LM_SECONDARY_FOCUS,
            focusVisible: tkn.LM_SECONDARY_FOCUS_VISIBLE,
            selected: tkn.LM_SECONDARY_SELECTED,
        },
        error: {
            main: tkn.LM_ERROR_MAIN,
            light: tkn.LM_ERROR_LIGHT,
            dark: tkn.LM_ERROR_DARK,
            contrastText: tkn.LM_ERROR_CONTRAST_TEXT,
            hover: tkn.LM_ERROR_HOVER,
            focus: tkn.LM_ERROR_FOCUS,
            focusVisible: tkn.LM_ERROR_FOCUS_VISIBLE,
            selected: tkn.LM_ERROR_SELECTED,
        },
        warning: {
            main: tkn.LM_WARNING_MAIN,
            light: tkn.LM_WARNING_LIGHT,
            dark: tkn.LM_WARNING_DARK,
            contrastText: tkn.LM_WARNING_CONTRAST_TEXT,
            hover: tkn.LM_WARNING_HOVER,
            focus: tkn.LM_WARNING_FOCUS,
            focusVisible: tkn.LM_WARNING_FOCUS_VISIBLE,
            selected: tkn.LM_WARNING_SELECTED,
        },
        info: {
            main: tkn.LM_INFO_MAIN,
            light: tkn.LM_INFO_LIGHT,
            dark: tkn.LM_INFO_DARK,
            contrastText: tkn.LM_INFO_CONTRAST_TEXT,
            hover: tkn.LM_INFO_HOVER,
            focus: tkn.LM_INFO_FOCUS,
            focusVisible: tkn.LM_INFO_FOCUS_VISIBLE,
            selected: tkn.LM_INFO_SELECTED,
        },
        success: {
            main: tkn.LM_SUCCESS_MAIN,
            light: tkn.LM_SUCCESS_LIGHT,
            dark: tkn.LM_SUCCESS_DARK,
            contrastText: tkn.LM_SUCCESS_CONTRAST_TEXT,
            hover: tkn.LM_SUCCESS_HOVER,
            focus: tkn.LM_SUCCESS_FOCUS,
            focusVisible: tkn.LM_SUCCESS_FOCUS_VISIBLE,
            selected: tkn.LM_SUCCESS_SELECTED,
        },
        neutral: {
            light: tkn.LM_NEUTRAL_LIGHT,
            twilight: tkn.LM_NEUTRAL_TWILIGHT,
            dark: tkn.LM_NEUTRAL_DARK,
            contrastText: tkn.LM_NEUTRAL_CONTRAST_TEXT,
        },
        text: {
            primary: tkn.LM_TEXT_PRIMARY,
            secondary: tkn.LM_TEXT_SECONDARY,
            disabled: tkn.LM_TEXT_DISABLED,
        },
        divider: tkn.LM_DIVIDER,
        background: {
            default: tkn.LM_BACKGROUND_DEFAULT,
            paper: tkn.LM_BACKGROUND_PAPER,
            paperLinearGradient: tkn.LM_BACKGROUND_PAPER_LINEAR_GRADIENT,
            overlay: tkn.LM_BACKGROUND_OVERLAY,
        },
        action: {
            active: tkn.LM_ACTION_ACTIVE,
            hover: tkn.LM_ACTION_HOVER,
            selected: tkn.LM_ACTION_SELECTED,
            focus: tkn.LM_ACTION_FOCUS,
            focusVisible: tkn.LM_ACTION_FOCUS_VISIBLE,
            disabled: tkn.LM_ACTION_DISABLED,
            disabledBackground: tkn.LM_ACTION_DISABLED_BACKGROUND,
            activated: tkn.LM_ACTION_ACTIVATED,
            dragged: tkn.DM_ACTION_DRAGGED,
        },
        brand: {
            main: tkn.BRAND_MAIN,
            contrastText: tkn.BRAND_CONTRAST_TEXT,
        },
    },
    constants: {
        border: '1px solid ' + tkn.LM_DIVIDER,
        borderRadius,
        borderSelected: '1px solid ' + tkn.LM_TEXT_PRIMARY,
        boxShadow,
        radiusCircle: '50%',
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        htmlFontSize: fontSizeBase,
        fontFamily: tkn.FontFamilyBase,
        fontWeightLight: light,
        fontWeightRegular: regular,
        fontWeightBold: bold,
        /*  relative font sizing
            using breakpoints.down so the first value is for desktop
            breakpoint value is for narrow screens */
        body1: {
            fontSize: fontSizeBody,
        },
        body2: {
            fontSize: fontSizeSmall,
        },
        subtitle1: {
            fontWeight: bold,
            lineHeight: 1.2,
        },
        subtitle2: {
            fontWeight: bold,
        },
        button: {
            fontWeight: bold,
        },
        h1: {
            fontSize: fontSizeH1,
            fontWeight: extraBold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeH2,
            },
        },
        h2: {
            fontSize: fontSizeH2,
            fontWeight: bold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeH3,
            },
        },
        h3: {
            fontSize: fontSizeH3,
            fontWeight: bold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeH4,
            },
        },
        h4: {
            fontSize: fontSizeH4,
            fontWeight: bold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeH5,
            },
        },
        h5: {
            fontSize: fontSizeH5,
            fontWeight: bold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeH6,
            },
        },
        h6: {
            fontSize: fontSizeH6,
            fontWeight: bold,
            [breakpoints.down('md')]: {
                fontSize: fontSizeBody,
            },
        },
        /* custom typography */
        overflowLine: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        overflowBlock: {
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'initial',
        },
        flexAlignCenter: {
            alignItems: 'center',
            display: 'flex',
        },
        radiusCircle: {
            backgroundClip: 'padding-box',
            borderRadius: '50%',
            overflow: 'hidden',
        },
    },
    components: {
        // ## global theme overrides
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    lineHeight: 1.43,
                },
            },
        },
        // ## layout and common elements
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: borderRadius,
                },
                elevation1: {
                    boxShadow: boxShadow,
                },
            },
        },
        // TODO: Audit with typography
        MuiTypography: {
            styleOverrides: {
                root: {
                    '&.extra-bold': {
                        fontWeight: extraBold,
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                icon: ({ theme }) => ({
                    '& .MuiSvgIcon-fontSizeMedium': {
                        fontSize: theme.typography.h6.fontSize,
                    },
                }),
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow,
                    border: 'none',
                    h3: {
                        fontWeight: bold,
                    },
                    '& .MuiCardContent-root': {
                        padding: 0,
                    },
                    '& .MuiCardActions-root': {
                        padding: 0,
                    },
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'hover',
            },
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                    '&:hover.no-underline': {
                        textDecoration: 'none',
                    },
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    '& li': {
                        fontSize: fontSizeBody,
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontSize: fontSizeSmall,
                    height: 18,
                    minWidth: 18,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    backgroundColor: theme.palette.text.primary,
                    color: theme.palette.primary.contrastText,
                    fontSize: fontSizeSmall,
                    maxWidth: 350,
                }),
            },
        },
        // ## dropdown fields
        MuiMenuItem: {
            styleOverrides: {
                // maybe needed
            },
        },
        // ## buttons
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    boxShadow,
                    borderRadius: borderRadius,
                    fontSize: fontSizeBody,
                    minHeight: buttonHeight,
                    minWidth: buttonHeight,
                    padding: buttonPadding,
                    '&:disabled': {
                        color: theme.palette.text.disabled,
                    },
                }),
                endIcon: ({ theme }) => ({
                    color: theme.palette.text.primary, // Temporary workaround for legacy components
                }),
                sizeSmall: {
                    minHeight: smallButtonHeight,
                    padding: smallButtonPadding,
                },
                text: {
                    boxShadow: 'none',
                    '&:disabled': {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.MuiRadio-root': {
                        '& .MuiSvgIcon-root': {
                            fontSize: fontSizeH5,
                        },
                    },
                    '&:focus-visible': {
                        backgroundColor: theme.palette.action.focus,
                    },
                }),
            },
            defaultProps: {
                // disable ripple on all buttons
                disableRipple: true,
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 4,
                    color: theme.palette.primary.light,
                    height: 24,
                    padding: spacing(1),
                    width: 24,
                }),
            },
        },
        // tabs list uses buttons
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        // ## forms
        MuiFormControl: {
            styleOverrides: {
                marginNormal: {
                    marginTop: spacing(1),
                },
                root: {
                    margin: spacing(1) + 'px 0',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: fontSizeBody,
                    height: 56,
                    width: 300,
                },
                multiline: {
                    minHeight: 82,
                },
                sizeSmall: {
                    height: 40,
                    width: 180,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    margin: spacing(1) + 'px 0',
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    marginLeft: spacing(1),
                    '& .MuiSvgIcon-root': {
                        height: 24,
                        width: 24,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    marginLeft: spacing(1),
                },
            },
        },
        // ## modal dialogs
        MuiDialog: {
            styleOverrides: {
                paper: {
                    minHeight: 240,
                    paddingTop: spacing(2),
                    paddingRight: spacing(2),
                    paddingBottom: spacing(1),
                    paddingLeft: spacing(3),
                    overflow: 'hidden',
                    minWidth: 580,
                    '& ol': {
                        paddingInlineStart: spacing(2.5),
                    },
                    '& li': {
                        marginBottom: spacing(0.5),
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: 0,
                    margin: 0,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    overflow: 'auto',
                    padding: spacing(0.5),
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    marginBottom: spacing(2),
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    marginBottom: spacing(1),
                    marginTop: spacing(2),
                    padding: 0,
                    '&>:not(:first-of-type)': {
                        marginLeft: spacing(2),
                    },
                },
            },
        },
        // added to get around the external modal overlay issue
        MuiModal: {
            styleOverrides: {
                root: {
                    marginRight: 'inherit',
                },
            },
        },
        // ## stepper
        MuiStepper: {
            styleOverrides: {
                horizontal: {
                    justifyContent: 'center',
                    marginBottom: spacing(3),
                    padding: spacing(0),
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-completed + .MuiStep-root .MuiStepConnector-line': {
                        borderColor: theme.palette.success.light,
                    },
                }),
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                horizontal: {
                    left: `calc(-50% + ${spacing(1)}px)`,
                    right: `calc(50% + ${spacing(1)}px)`,
                },
                lineHorizontal: ({ theme }) => ({
                    borderColor: theme.palette.success.light,
                    borderTopWidth: '2px',
                }),
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...theme.typography.radiusCircle,
                    height: 14,
                    width: 14,
                    border: `2px solid ${theme.palette.success.light}`,
                    color: theme.palette.background.paper,
                    '&.Mui-active': {
                        border: `2px solid ${theme.palette.success.light}`,
                        '&.MuiSvgIcon-root': {
                            color: theme.palette.background.paper,
                        },
                    },
                    '&.Mui-completed': {
                        border: 'none',
                        color: theme.palette.success.light,
                        height: 20,
                        width: 20,
                    },
                }),
                text: {
                    display: 'none',
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                iconContainer: {
                    transform: `translateY(${spacing(0.75)}px)`,
                    zIndex: 100,
                    '&.Mui-completed': {
                        transform: `translateY(${spacing(0.5)}px)`,
                    },
                },
            },
        },
    },
});

export const stitchDark = createTheme({
    constants: {
        ...stitchLight.constants,
        border: '1px solid' + tkn.DM_DIVIDER,
        borderSelected: '1px solid ' + tkn.DM_TEXT_PRIMARY,
    },
    breakpoints,
    palette: {
        mode: 'dark',
        common: {
            black: tkn.COMMON_BLACK,
            white: tkn.COMMON_WHITE,
        },
        primary: {
            light: tkn.LM_PRIMARY_LIGHT,
            main: tkn.DM_PRIMARY_MAIN,
            dark: tkn.DM_PRIMARY_DARK,
            contrastText: tkn.DM_PRIMARY_CONTRAST_TEXT,
            hover: tkn.DM_PRIMARY_HOVER,
            focus: tkn.DM_PRIMARY_FOCUS,
            focusVisible: tkn.DM_PRIMARY_FOCUS_VISIBLE,
            selected: tkn.DM_PRIMARY_SELECTED,
        },
        secondary: {
            light: tkn.DM_SECONDARY_LIGHT,
            main: tkn.DM_SECONDARY_MAIN,
            dark: tkn.DM_SECONDARY_DARK,
            contrastText: tkn.DM_SECONDARY_CONTRAST_TEXT,
            hover: tkn.DM_SECONDARY_HOVER,
            focus: tkn.DM_SECONDARY_FOCUS,
            focusVisible: tkn.DM_SECONDARY_FOCUS_VISIBLE,
            selected: tkn.DM_SECONDARY_SELECTED,
        },
        error: {
            light: tkn.DM_ERROR_LIGHT,
            main: tkn.DM_ERROR_MAIN,
            dark: tkn.DM_ERROR_DARK,
            contrastText: tkn.DM_ERROR_CONTRAST_TEXT,
            hover: tkn.DM_ERROR_HOVER,
            focus: tkn.DM_ERROR_FOCUS,
            focusVisible: tkn.DM_ERROR_FOCUS_VISIBLE,
            selected: tkn.DM_ERROR_SELECTED,
        },
        warning: {
            light: tkn.DM_WARNING_LIGHT,
            main: tkn.DM_WARNING_MAIN,
            dark: tkn.DM_WARNING_DARK,
            contrastText: tkn.DM_WARNING_CONTRAST_TEXT,
            hover: tkn.DM_WARNING_HOVER,
            focus: tkn.DM_WARNING_FOCUS,
            focusVisible: tkn.DM_WARNING_FOCUS_VISIBLE,
            selected: tkn.DM_WARNING_SELECTED,
        },
        info: {
            light: tkn.DM_INFO_LIGHT,
            main: tkn.DM_INFO_MAIN,
            dark: tkn.DM_INFO_DARK,
            contrastText: tkn.DM_INFO_CONTRAST_TEXT,
            hover: tkn.DM_INFO_HOVER,
            focus: tkn.DM_INFO_FOCUS,
            focusVisible: tkn.DM_INFO_FOCUS_VISIBLE,
            selected: tkn.DM_INFO_SELECTED,
        },
        success: {
            light: tkn.DM_SUCCESS_LIGHT,
            main: tkn.DM_SUCCESS_MAIN,
            dark: tkn.DM_SUCCESS_DARK,
            contrastText: tkn.DM_SUCCESS_CONTRAST_TEXT,
            hover: tkn.DM_SUCCESS_HOVER,
            focus: tkn.DM_SUCCESS_FOCUS,
            focusVisible: tkn.DM_SUCCESS_FOCUS_VISIBLE,
            selected: tkn.DM_SUCCESS_SELECTED,
        },
        neutral: {
            light: tkn.DM_NEUTRAL_LIGHT,
            twilight: tkn.DM_NEUTRAL_TWILIGHT,
            dark: tkn.DM_NEUTRAL_DARK,
            contrastText: tkn.DM_NEUTRAL_CONTRAST_TEXT,
        },
        text: {
            primary: tkn.DM_TEXT_PRIMARY,
            secondary: tkn.DM_TEXT_SECONDARY,
            disabled: tkn.DM_TEXT_DISABLED,
        },
        divider: tkn.DM_DIVIDER,
        background: {
            default: tkn.DM_BACKGROUND_DEFAULT,
            paper: tkn.DM_BACKGROUND_PAPER,
            overlay: tkn.DM_BACKGROUND_OVERLAY,
            paperLinearGradient: tkn.DM_BACKGROUND_PAPER_LINEAR_GRADIENT,
        },
        action: {
            active: tkn.DM_ACTION_ACTIVE,
            focusVisible: tkn.DM_ACTION_FOCUS_VISIBLE,
            hover: tkn.DM_ACTION_HOVER,
            selected: tkn.DM_ACTION_SELECTED,
            focus: tkn.DM_ACTION_FOCUS,
            disabled: tkn.DM_ACTION_DISABLED,
            disabledBackground: tkn.DM_ACTION_DISABLED_BACKGROUND,
            activated: tkn.DM_ACTION_ACTIVATED,
            dragged: tkn.DM_ACTION_DRAGGED,
        },
        brand: {
            main: tkn.BRAND_MAIN,
            contrastText: tkn.BRAND_CONTRAST_TEXT,
        },
    },
    typography: {
        ...stitchLight.typography,
    },
    components: {
        ...stitchLight.components,
    },
});
